import { yupResolver } from '@hookform/resolvers/yup'
import CancelIcon from '@mui/icons-material/Cancel'
import ModeEditIcon from '@mui/icons-material/ModeEdit'
import SaveAsIcon from '@mui/icons-material/SaveAs'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import { Status } from 'interface'
import { useSnackbar } from 'notistack'
import { useEffect, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import axiosClient from 'utils/axiosClient'
import * as yup from 'yup'

type Props = {
  apiEndpoint?: string
  label?: string
  status: Status
  my: number
}
interface FormInput {
  status: Status
}

export default function EditStatus({ apiEndpoint, label, status, my }: Props) {
  const { enqueueSnackbar } = useSnackbar()
  const schema = yup.object()
  const {
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
  } = useForm<FormInput>({
    resolver: yupResolver(schema),
    defaultValues: {
      status,
    },
  })
  const [loading, setLoading] = useState(false)
  const [edit, setEdit] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  const handleKeyDown = (e: globalThis.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setEdit(false)
      setValue('status', status, {
        shouldValidate: true,
      })
    }
  }
  useEffect(() => {
    if (ref.current) {
      if (edit) {
        ref.current.addEventListener('keydown', (e) => {
          handleKeyDown(e)
        })
      } else {
        ref.current.removeEventListener('keydown', (e) => {
          handleKeyDown(e)
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit])
  const onSubmit: SubmitHandler<FormInput> = async (data) => {
    if (apiEndpoint) {
      setLoading(true)
      try {
        await axiosClient.put(apiEndpoint, {
          data: JSON.stringify(data),
        })
        setEdit(false)
        const msg = 'ステータスを修正しました。'
        enqueueSnackbar(msg, {
          variant: 'success',
        })
        setLoading(false)
      } catch (_error) {
        const msg = 'エラーが発生しました。'
        enqueueSnackbar(msg, {
          variant: 'error',
        })
      }
    }
  }
  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      onSubmit={handleSubmit(onSubmit)}
      my={my}
    >
      {edit ? (
        <Stack
          direction={{ md: 'row', xs: 'column' }}
          alignItems="center"
          sx={{ display: edit ? 'flex' : 'none' }}
          ref={ref}
        >
          <FormControl>
            <InputLabel>ステータス</InputLabel>
            <Select
              value={getValues('status')}
              label="ステータス"
              onChange={(event) => {
                setValue('status', event.target.value as Status, {
                  shouldValidate: true,
                })
              }}
            >
              <MenuItem value={'preActive'}>登録済み</MenuItem>
              <MenuItem value={'active'}>利用中</MenuItem>
              <MenuItem value={'deleted'}>削除済み</MenuItem>
            </Select>
          </FormControl>
          <Stack direction="row">
            <Button
              sx={{
                mx: 1,
                px: 1,
                minWidth: 0,
              }}
              size="small"
              color="error"
              variant="contained"
              onClick={() => {
                setEdit(false)
              }}
            >
              <CancelIcon />
            </Button>
            <LoadingButton
              disabled={Object.keys(errors).length > 0}
              loading={loading}
              sx={{
                mx: 1,
                px: 1,
                minWidth: 0,
              }}
              size="small"
              variant="contained"
              type="submit"
            >
              <SaveAsIcon />
            </LoadingButton>
          </Stack>
        </Stack>
      ) : (
        <Stack
          direction={{ md: 'row', xs: 'column' }}
          spacing={2}
          sx={{ display: !edit ? 'flex' : 'none' }}
          alignItems={{
            md: 'center',
            xs: 'flex-start',
          }}
        >
          <Typography fontWeight="bold" variant="body2" flex={'none'}>
            {label}
          </Typography>
          <Stack direction="row" alignItems="center">
            <Typography>
              {
                {
                  preActive: '登録済み',
                  active: '利用中',
                  deleted: '削除済み',
                }[status]
              }
            </Typography>
            <Button
              sx={{
                flex: 'none',
                mx: 1,
                p: 0.5,
                minWidth: 0,
              }}
              size="small"
              variant="contained"
              color="info"
              onClick={() => {
                setEdit(true)
              }}
            >
              <ModeEditIcon fontSize="small" />
            </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  )
}
